


export const convertDocDefinitionToHTML = (docDefinition, options = {}) => {
  if (!docDefinition || !Array.isArray(docDefinition.content)) {
    return '<div style="font-family: Arial, sans-serif; padding: 20px;">No valid content available</div>';
  }

  const {
    includeStyles = true,
    pageTitle = 'Report',
    usePrintMediaQuery = true,
    allowScaling = false,
  } = options;

  const pageMargins = docDefinition.pageMargins || [40, 60, 40, 60];
  const defaultFontSize = docDefinition.defaultStyle?.fontSize || 10;
  const defaultLineHeight = docDefinition.defaultStyle?.lineHeight || 1.25;

  let html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${pageTitle}</title>
  <meta name="viewport" content="width=device-width, initial-scale=1.0">`;

  if (includeStyles) {
    html += `
    <style>
      /* Reset and base styles */
      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }

      body {
        font-family: "Helvetica", Arial, sans-serif !important;
        margin: ${pageMargins[3]}pt ${pageMargins[2]}pt ${pageMargins[1]}pt ${pageMargins[0]}pt !important;
        padding: 0 !important;
        font-size: ${defaultFontSize}pt !important;
        line-height: ${defaultLineHeight} !important;
        color: #000;
        background-color: white !important;
        word-wrap: break-word;
      }

      /* All content blocks with precise spacing */
      .pdf-block {
        display: block;
        font-size: inherit;
        line-height: inherit;
        margin: 0;
        padding: 0;
      }

      /* Paragraph spacing - match PDFMake's [0, 0, 0, 8] margin */
      .pdf-paragraph {
        margin-bottom: 8pt !important;
        display: block;
      }

      /* Text styling classes */
      .bold { font-weight: bold !important; }
      .italic { font-style: italic !important; }
      .underline { text-decoration: underline !important; }
      .line-through { text-decoration: line-through !important; }

      /* Preserve whitespace and newlines exactly */
      .preserve-space {
        white-space: pre-wrap !important;
        word-wrap: break-word !important;
      }

      /* Enhanced columns with precise spacing */
      .pdf-columns {
        display: flex;
        flex-wrap: nowrap;
        width: 100%;
        align-items: flex-start;
      }

      .pdf-column {
        flex: 1 1 auto;
        min-width: 0;
        word-wrap: break-word;
      }

      /* Patient info specific spacing */
      .patient-info-item {
        margin-bottom: 5pt !important;
        display: block;
        line-height: 1.2 !important;
      }

      /* Tables with exact spacing */
      .pdf-table {
        width: 100% !important;
        border-collapse: collapse !important;
        table-layout: auto;
        margin-bottom: 8pt !important;
      }

      .pdf-table th,
      .pdf-table td {
        border: 0.5px solid #000 !important;
        vertical-align: top !important;
        word-wrap: break-word !important;
        box-sizing: border-box;
        font-size: inherit;
        line-height: inherit;
        padding: 3pt !important;
      }

      .pdf-table th {
        font-weight: bold !important;
        text-align: center !important;
      }

      /* Lists with proper spacing */
      ul.pdf-list, ol.pdf-list {
        list-style-position: outside;
        margin-bottom: 8pt !important;
        padding-left: 20pt !important;
      }

      ul.pdf-list li, ol.pdf-list li {
        padding: 0;
        margin-bottom: 0 !important;
      }

      /* Divider with precise margins */
      .pdf-divider {
        border: 0;
        border-top: 1px solid #000 !important;
        width: 100%;
        margin: 10pt 0 20pt 0 !important;
      }

      /* Images */
      .pdf-image {
        max-width: 100% !important;
        height: auto !important;
        display: block;
      }

      /* Specific margin classes for precise control */
      .margin-0-0-0-8 { margin: 0 0 8pt 0 !important; }
      .margin-0-0-0-5 { margin: 0 0 5pt 0 !important; }
      .margin-0-0-0-20 { margin: 0 0 20pt 0 !important; }
      .margin-0-20-0-0 { margin: 0 0 0 20pt !important; }

      /* Footer positioning */
      .pdf-footer {
        position: fixed;
        bottom: ${pageMargins[1]}pt;
        left: ${pageMargins[0]}pt;
        right: ${pageMargins[2]}pt;
        text-align: center;
        font-size: 8pt;
        color: #666;
      }

      /* Print-specific styles */
      @media print {
        @page {
        @bottom-center {
    content: "Page " counter(page);
    font-size: 8pt;
    color: #666;
  }
        }

        body {
          margin: ${pageMargins[3]}pt ${pageMargins[2]}pt ${pageMargins[1]}pt ${pageMargins[0]}pt !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        /* Ensure spacing is preserved in print */
        .pdf-paragraph {
          margin-bottom: 8pt !important;
        }

        .patient-info-item {
          margin-bottom: 5pt !important;
        }

        .pdf-table {
          page-break-inside: avoid;
        }

        .pdf-divider {
          margin: 10pt 0 20pt 0 !important;
        }

        /* Hide browser UI */
        header, footer, nav, aside { display: none !important; }
      }

      /* Page break handling */
      .page-break-before { page-break-before: always; }
      .page-break-after { page-break-after: always; }
      .page-break-inside-avoid { page-break-inside: avoid; }
    </style>`;
  }

  html += `
</head>
<body>`;

  // Process content recursively with enhanced spacing
  docDefinition.content.forEach(item => {
    html += processContentItem(item, docDefinition, allowScaling);
  });

  // Add footer if defined
  if (docDefinition.footer) {
    html += `<div class="pdf-footer">`;
    if (typeof docDefinition.footer === 'function') {
      const footerDef = docDefinition.footer(1, 1);
      if (Array.isArray(footerDef)) {
        footerDef.forEach(item => {
          if (item) html += processContentItem(item, docDefinition, allowScaling);
        });
      } else if (footerDef) {
        html += processContentItem(footerDef, docDefinition, allowScaling);
      }
    } else if (Array.isArray(docDefinition.footer)) {
      docDefinition.footer.forEach(item => {
        if (item) html += processContentItem(item, docDefinition, allowScaling);
      });
    }
    html += `</div>`;
  }

  html += `
</body>
</html>`;

  return html;
};

// --- Core Processing Functions ---

const processContentItem = (item, docDefinition, allowScaling = false) => {
  if (!item) return '';

  try {
    // Handle page break
    if (item.pageBreak) {
      if (allowScaling) {
        return ''; // Skip page break for scaling mode
      }
      return '<div class="page-break-before"></div>';
    }

    // Text
    if (item.text !== undefined) {
      return processTextItem(item);
    }

    // Columns
    if (item.columns && Array.isArray(item.columns)) {
      return processColumnsItem(item, docDefinition, allowScaling);
    }

    // Canvas (lines)
    if (item.canvas && Array.isArray(item.canvas)) {
      return processCanvasItem(item);
    }

    // Stack
    if (item.stack && Array.isArray(item.stack)) {
      return processStackItem(item, docDefinition, allowScaling);
    }

    // Table
    if (item.table && item.table.body) {
      return processTableItem(item, docDefinition, allowScaling);
    }

    // Lists
    if (item.ul && Array.isArray(item.ul)) {
      return processList(item.ul, 'ul', item, docDefinition, allowScaling);
    }
    if (item.ol && Array.isArray(item.ol)) {
      return processList(item.ol, 'ol', item, docDefinition, allowScaling);
    }

    // Image
    if (item.image) {
      return processImageItem(item);
    }

    // Plain string fallback
    if (typeof item === 'string') {
      return `<div class="pdf-block preserve-space">${escapeHtml(item)}</div>`;
    }

    return '';

  } catch (error) {
    console.error('Error processing item:', item, error);
    return `<div style="color: red; border: 1px solid red; padding: 5pt; font-size: 8pt;">ERROR: ${error.message}</div>`;
  }
};

// --- Enhanced Text Processor ---
const processTextItem = (item) => {
  let content = '';
  let tag = 'div';
  const classes = ['pdf-block'];
  const styles = [];

  // Determine if this is a patient info item (based on content patterns)
  const isPatientInfoItem = item.text && typeof item.text === 'string' && 
    (item.text.includes('Register No:') || item.text.includes('Patient Name:') || 
     item.text.includes('Age:') || item.text.includes('Bill/Advice No:') ||
     item.text.includes('Report No:') || item.text.includes('Doctor:') ||
     item.text.includes('Observed Date:'));

  if (isPatientInfoItem) {
    classes.push('patient-info-item');
  }

  // Determine tag based on font size (preserve heading hierarchy)
  if (item.fontSize > 18) tag = 'h1';
  else if (item.fontSize > 16) tag = 'h2';
  else if (item.fontSize > 14) tag = 'h3';
  else if (item.fontSize > 12) tag = 'h4';

  // Apply styles via classes and inline styles
  if (item.bold) classes.push('bold');
  if (item.italics) classes.push('italic');
  if (item.decoration === 'underline') classes.push('underline');
  if (item.decoration === 'lineThrough') classes.push('line-through');

  // Preserve whitespace if needed
  if (item.preserveLeadingSpaces || (typeof item.text === 'string' && /\s{2,}/.test(item.text))) {
    classes.push('preserve-space');
  }

  // Enhanced margin handling - convert PDFMake margins to CSS exactly
  if (item.margin) {
    const marginCSS = convertPDFMakeMargin(item.margin);
    styles.push(`margin: ${marginCSS}`);
    
    // Add specific margin class for common patterns
    if (Array.isArray(item.margin) && 
        item.margin[0] === 0 && item.margin[1] === 0 && 
        item.margin[2] === 0 && item.margin[3] === 8) {
      classes.push('margin-0-0-0-8');
    } else if (Array.isArray(item.margin) && 
               item.margin[0] === 0 && item.margin[1] === 0 && 
               item.margin[2] === 0 && item.margin[3] === 5) {
      classes.push('margin-0-0-0-5');
    }
  } else {
    // Default paragraph spacing if no margin specified
    classes.push('pdf-paragraph');
  }

  // Alignment
  if (item.alignment) {
    styles.push(`text-align: ${item.alignment}`);
  }

  // Font size (exact conversion)
  if (item.fontSize) {
    styles.push(`font-size: ${item.fontSize}pt !important`);
  }

  // Line height
  if (item.lineHeight) {
    styles.push(`line-height: ${item.lineHeight} !important`);
  }

  // Color
  if (item.color) {
    styles.push(`color: ${item.color}`);
  }

  // Background color
  if (item.background) {
    styles.push(`background-color: ${item.background}`);
  }

  // Width and height
  if (item.width) {
    if (typeof item.width === 'string') {
      styles.push(`width: ${item.width}`);
    } else {
      styles.push(`width: ${item.width}pt`);
    }
  }

  if (item.height) {
    if (typeof item.height === 'string') {
      styles.push(`height: ${item.height}`);
    } else {
      styles.push(`height: ${item.height}pt`);
    }
  }

  const classAttr = classes.length ? ` class="${classes.join(' ')}"` : '';
  const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';

  // Handle text content
  if (typeof item.text === 'string') {
    content = escapeHtml(item.text);
  } else if (Array.isArray(item.text)) {
    content = item.text.map(part => {
      if (typeof part === 'string') {
        return escapeHtml(part);
      } else if (part && typeof part === 'object') {
        const partClasses = [];
        const partStyles = [];

        if (part.bold) partClasses.push('bold');
        if (part.italics) partClasses.push('italic');
        if (part.decoration === 'underline') partClasses.push('underline');
        if (part.decoration === 'lineThrough') partClasses.push('line-through');
        if (part.color) partStyles.push(`color: ${part.color}`);
        if (part.background) partStyles.push(`background-color: ${part.background}`);
        if (part.fontSize) partStyles.push(`font-size: ${part.fontSize}pt`);

        const partClassAttr = partClasses.length ? ` class="${partClasses.join(' ')}"` : '';
        const partStyleAttr = partStyles.length ? ` style="${partStyles.join('; ')}"` : '';

        return `<span${partClassAttr}${partStyleAttr}>${escapeHtml(part.text || '')}</span>`;
      }
      return '';
    }).join('');
  }

  return `<${tag}${classAttr}${styleAttr}>${content}</${tag}>`;
};

// --- Enhanced Columns Processor ---
const processColumnsItem = (item, docDefinition, allowScaling = false) => {
  const styles = [];

  // Enhanced margin handling for columns
  if (item.margin) {
    const marginCSS = convertPDFMakeMargin(item.margin);
    styles.push(`margin: ${marginCSS}`);
  } else {
    // Default margin for column containers
    styles.push(`margin-bottom: 20pt`);
  }

  // Column gap handling
  if (item.columnGap !== undefined) {
    styles.push(`gap: ${item.columnGap}pt`);
  }

  const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';

  let html = `<div class="pdf-columns"${styleAttr}>`;

  item.columns.forEach(col => {
    const colStyles = [];
    let colClass = 'pdf-column';

    // Exact width handling
    if (col.width) {
      if (typeof col.width === 'string') {
        if (col.width === '*') {
          colStyles.push('flex: 1 1 auto');
        } else if (col.width === 'auto') {
          colStyles.push('flex: 0 0 auto');
        } else if (col.width.endsWith('%')) {
          colStyles.push(`flex: 0 0 ${col.width}`);
          colStyles.push(`max-width: ${col.width}`);
        } else {
          colStyles.push(`flex: 0 0 ${col.width}`);
          colStyles.push(`max-width: ${col.width}`);
        }
      } else if (typeof col.width === 'number') {
        colStyles.push(`flex: 0 0 ${col.width}pt`);
        colStyles.push(`max-width: ${col.width}pt`);
      }
    }

    // Alignment
    if (col.alignment) {
      colStyles.push(`text-align: ${col.alignment}`);
    }

    // Margin
    if (col.margin) {
      const margin = convertPDFMakeMargin(col.margin);
      colStyles.push(`margin: ${margin}`);
    }

    const colStyleAttr = colStyles.length ? ` style="${colStyles.join('; ')}"` : '';

    // Process column content
    let colContent = '';
    if (col.text !== undefined) {
      colContent = processTextItem(col);
    } else if (col.stack) {
      colContent = processStackItem(col, docDefinition, allowScaling);
    } else if (col.table) {
      colContent = processTableItem(col, docDefinition, allowScaling);
    } else {
      colContent = processContentItem(col, docDefinition, allowScaling);
    }

    html += `<div class="${colClass}"${colStyleAttr}>${colContent}</div>`;
  });

  html += `</div>`;
  return html;
};

// --- Enhanced Canvas Processor (Lines) ---
const processCanvasItem = (item) => {
  if (!item.canvas || item.canvas.length === 0) return '';

  const line = item.canvas[0];
  if (line.type === 'line') {
    const styles = [];

    // Enhanced margin handling for dividers
    if (item.margin) {
      const marginCSS = convertPDFMakeMargin(item.margin);
      styles.push(`margin: ${marginCSS}`);
    } else {
      // Default divider margins to match PDFMake spacing
      styles.push(`margin: 10pt 0 20pt 0`);
    }

    // Line width
    if (line.lineWidth) {
      styles.push(`border-top-width: ${line.lineWidth}px`);
    }

    // Line color
    if (line.lineColor) {
      styles.push(`border-top-color: ${line.lineColor}`);
    }

    const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';
    return `<hr class="pdf-divider"${styleAttr}>`;
  }

  return '';
};

// --- Enhanced Stack Processor ---
const processStackItem = (item, docDefinition, allowScaling = false) => {
  const styles = [];

  // Exact margin handling
  if (item.margin) {
    const margin = convertPDFMakeMargin(item.margin);
    styles.push(`margin: ${margin}`);
  }

  // Alignment
  if (item.alignment) {
    styles.push(`text-align: ${item.alignment}`);
  }

  const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';

  let html = `<div${styleAttr}>`;
  if (Array.isArray(item.stack)) {
    item.stack.forEach(child => {
      html += processContentItem(child, docDefinition, allowScaling);
    });
  }
  html += `</div>`;
  return html;
};

// --- Enhanced Table Processor ---
const processTableItem = (item, docDefinition, allowScaling = false) => {
  if (!item.table || !item.table.body) return '';

  const styles = [];

  // Exact margin handling
  if (item.margin) {
    const margin = convertPDFMakeMargin(item.margin);
    styles.push(`margin: ${margin}`);
  }

  // Table width
  if (item.table.width) {
    if (typeof item.table.width === 'string') {
      styles.push(`width: ${item.table.width}`);
    } else {
      styles.push(`width: ${item.table.width}pt`);
    }
  }

  const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';

  let html = `<table class="pdf-table"${styleAttr}>`;

  const headerRows = item.table.headerRows || 0;
  const columnWidths = item.table.widths || [];

  // Create colgroup for exact column widths
  if (columnWidths.length > 0) {
    html += '<colgroup>';
    columnWidths.forEach(width => {
      if (typeof width === 'number') {
        html += `<col style="width: ${width}pt;">`;
      } else if (typeof width === 'string') {
        html += `<col style="width: ${width};">`;
      } else {
        html += '<col style="width: auto;">';
      }
    });
    html += '</colgroup>';
  }

  // Header
  if (headerRows > 0) {
    html += '<thead>';
    for (let i = 0; i < headerRows; i++) {
      html += '<tr>';
      item.table.body[i].forEach((cell, colIndex) => {
        const cellStyles = [];

        // Extract layout properties for cell padding
        if (item.layout) {
          const paddingTop = typeof item.layout.paddingTop === 'function'
            ? item.layout.paddingTop(i, colIndex)
            : (item.layout.paddingTop || 5);
          const paddingBottom = typeof item.layout.paddingBottom === 'function'
            ? item.layout.paddingBottom(i, colIndex)
            : (item.layout.paddingBottom || 5);
          const paddingLeft = typeof item.layout.paddingLeft === 'function'
            ? item.layout.paddingLeft(i, colIndex)
            : (item.layout.paddingLeft || 3);
          const paddingRight = typeof item.layout.paddingRight === 'function'
            ? item.layout.paddingRight(i, colIndex)
            : (item.layout.paddingRight || 3);

          cellStyles.push(`padding: ${paddingTop}pt ${paddingRight}pt ${paddingBottom}pt ${paddingLeft}pt`);
        }

        // Min cell height
        if (cell.minCellHeight) {
          cellStyles.push(`min-height: ${cell.minCellHeight}pt`);
        }

        const cellStyleAttr = cellStyles.length ? ` style="${cellStyles.join('; ')}"` : '';
        html += `<th${cellStyleAttr}>${processTableCell(cell, docDefinition, allowScaling)}</th>`;
      });
      html += '</tr>';
    }
    html += '</thead>';
  }

  // Body
  html += '<tbody>';
  for (let i = headerRows; i < item.table.body.length; i++) {
    html += '<tr>';
    item.table.body[i].forEach((cell, colIndex) => {
      const cellStyles = [];

      // Extract layout properties for cell padding
      if (item.layout) {
        const paddingTop = typeof item.layout.paddingTop === 'function'
          ? item.layout.paddingTop(i, colIndex)
          : (item.layout.paddingTop || 5);
        const paddingBottom = typeof item.layout.paddingBottom === 'function'
          ? item.layout.paddingBottom(i, colIndex)
          : (item.layout.paddingBottom || 5);
        const paddingLeft = typeof item.layout.paddingLeft === 'function'
          ? item.layout.paddingLeft(i, colIndex)
          : (item.layout.paddingLeft || 3);
        const paddingRight = typeof item.layout.paddingRight === 'function'
          ? item.layout.paddingRight(i, colIndex)
          : (item.layout.paddingRight || 3);

        cellStyles.push(`padding: ${paddingTop}pt ${paddingRight}pt ${paddingBottom}pt ${paddingLeft}pt`);
      }

      // Min cell height
      if (cell.minCellHeight) {
        cellStyles.push(`min-height: ${cell.minCellHeight}pt`);
      }

      // Cell alignment
      if (cell.alignment) {
        cellStyles.push(`text-align: ${cell.alignment}`);
      }

      const cellStyleAttr = cellStyles.length ? ` style="${cellStyles.join('; ')}"` : '';
      html += `<td${cellStyleAttr}>${processTableCell(cell, docDefinition, allowScaling)}</td>`;
    });
    html += '</tr>';
  }
  html += '</tbody>';

  html += '</table>';
  return html;
};

// --- Enhanced Table Cell Processor ---
const processTableCell = (cell, docDefinition, allowScaling = false) => {
  if (typeof cell === 'string') {
    return `<div class="preserve-space">${escapeHtml(cell)}</div>`;
  }

  if (cell && typeof cell === 'object') {
    if (cell.text !== undefined) return processTextItem(cell);
    if (cell.stack) return processStackItem(cell, docDefinition, allowScaling);
    if (cell.table) return processTableItem(cell, docDefinition, allowScaling);
    if (cell.ul) return processList(cell.ul, 'ul', cell, docDefinition, allowScaling);
    if (cell.ol) return processList(cell.ol, 'ol', cell, docDefinition, allowScaling);
    if (cell.image) return processImageItem(cell);
  }

  return processContentItem(cell, docDefinition, allowScaling);
};

// --- Enhanced List Processor ---
const processList = (items, type, parentItem, docDefinition, allowScaling = false) => {
  if (!Array.isArray(items)) return '';

  const styles = [];

  // Exact margin handling from parent
  if (parentItem && parentItem.margin) {
    const margin = convertPDFMakeMargin(parentItem.margin);
    styles.push(`margin: ${margin}`);
  }

  // Padding
  if (parentItem && parentItem.paddingLeft) {
    styles.push(`padding-left: ${parentItem.paddingLeft}pt`);
  } else {
    styles.push(`padding-left: 20pt`);
  }

  const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';

  let html = `<${type} class="pdf-list"${styleAttr}>`;
  items.forEach(item => {
    const liStyles = [];

    // Item margin
    if (item && item.margin) {
      const margin = convertPDFMakeMargin(item.margin);
      liStyles.push(`margin: ${margin}`);
    }

    const liStyleAttr = liStyles.length ? ` style="${liStyles.join('; ')}"` : '';

    html += `<li${liStyleAttr}>`;
    if (typeof item === 'string') {
      html += `<div class="preserve-space">${escapeHtml(item)}</div>`;
    } else if (item && typeof item === 'object') {
      html += processContentItem(item, docDefinition, allowScaling);
    }
    html += '</li>';
  });
  html += `</${type}>`;
  return html;
};

// --- Enhanced Image Processor ---
const processImageItem = (item) => {
  if (!item.image) return '';

  const styles = [];

  // Exact dimensions
  if (item.width) styles.push(`width: ${item.width}px`);
  if (item.height) styles.push(`height: ${item.height}px`);

  // Fit property
  if (item.fit && Array.isArray(item.fit)) {
    styles.push(`max-width: ${item.fit[0]}px`);
    styles.push(`max-height: ${item.fit[1]}px`);
    styles.push(`object-fit: contain`);
  }

  // Exact margin handling
  if (item.margin) {
    const margin = convertPDFMakeMargin(item.margin);
    styles.push(`margin: ${margin}`);
  }

  const styleAttr = styles.length ? ` style="${styles.join('; ')}"` : '';

  return `<img src="${escapeHtml(item.image)}" class="pdf-image"${styleAttr} alt="" />`;
};

// --- Utility Functions ---

// Convert PDFMake margins [left, top, right, bottom] to CSS [top, right, bottom, left]
const convertPDFMakeMargin = (margin) => {
  if (Array.isArray(margin)) {
    // PDFMake uses [left, top, right, bottom]
    // CSS uses [top, right, bottom, left]  
    const [left, top, right, bottom] = margin;
    return `${top || 0}pt ${right || 0}pt ${bottom || 0}pt ${left || 0}pt`;
  } else if (typeof margin === 'number') {
    return `${margin}pt`;
  } else {
    return margin;
  }
};

// Escape HTML
const escapeHtml = (unsafe) => {
  if (typeof unsafe !== 'string') return String(unsafe);
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};